import React from 'react';
import { WebUiButton } from 'src/modules/web-ui/components/button';
import { WebUiInput } from 'src/modules/web-ui/components/input';
import { WebUiSelect } from 'src/modules/web-ui/components/select';

import { postWebUiApi } from '../../../api';
import type { DnsClient } from './c-system-settings-types';

const DNS_TIMEOUT_OPTS = Array.from({ length: 30 }, (_, i) => i + 1);
const DNS_RETRY_OPTS = [1, 2, 3, 4, 5];

type Props = {
  readonly: boolean;
  dns: DnsClient;
  setDns: React.Dispatch<React.SetStateAction<DnsClient>>;
  runSet: (fn: () => Promise<void>, ok: string) => void;
};

export default function CSystemDnsPanel({
  readonly,
  dns,
  setDns,
  runSet,
}: Props) {
  const dnsParams: DnsClient = {
    enabled: Boolean(dns.enabled),
    server_primary: dns.server_primary ?? '',
    server_secondary: dns.server_secondary ?? '',
    domain_name: dns.domain_name ?? '',
    dns_query_timeout: Number(dns.dns_query_timeout ?? 1),
    dns_query_retries: Number(dns.dns_query_retries ?? 1),
    dns_over_tcp: Boolean(dns.dns_over_tcp),
  };

  return (
    <div className="flex flex-col gap-2">
      <h4 className="font-bold">DNS Client</h4>
      <table className="webui-table w-full">
        <thead>
          <tr className="webui-table-row-head">
            <th>Name</th>
            <th>Setting</th>
          </tr>
        </thead>
        <tbody>
          <tr className="webui-table-row-content">
            <td>Enabled</td>
            <td>
              <WebUiInput
                type="checkbox"
                disabled={readonly}
                checked={Boolean(dns.enabled)}
                onChange={(e) =>
                  setDns((p) => ({ ...p, enabled: e.target.checked }))
                }
              />
            </td>
          </tr>
          <tr className="webui-table-row-content">
            <td>DNS Server Primary</td>
            <td>
              <WebUiInput
                className="w-full"
                disabled={readonly}
                value={dns.server_primary ?? ''}
                onChange={(e) =>
                  setDns((p) => ({ ...p, server_primary: e.target.value }))
                }
              />
            </td>
          </tr>
          <tr className="webui-table-row-content">
            <td>DNS Server Secondary</td>
            <td>
              <WebUiInput
                className="w-full"
                inputSize="sm"
                disabled={readonly}
                value={dns.server_secondary ?? ''}
                onChange={(e) =>
                  setDns((p) => ({
                    ...p,
                    server_secondary: e.target.value,
                  }))
                }
              />
            </td>
          </tr>
          <tr className="webui-table-row-content">
            <td>Domain Search Name</td>
            <td>
              <WebUiInput
                className="w-full"
                inputSize="sm"
                disabled={readonly}
                value={dns.domain_name ?? ''}
                onChange={(e) =>
                  setDns((p) => ({ ...p, domain_name: e.target.value }))
                }
              />
            </td>
          </tr>
          <tr className="webui-table-row-content">
            <td>Timeout</td>
            <td>
              <WebUiSelect
                className="w-[72px]"
                inputSize="sm"
                disabled={readonly}
                value={dns.dns_query_timeout?.toString() ?? '1'}
                options={DNS_TIMEOUT_OPTS.map((n) => ({
                  label: n.toString(),
                  value: n.toString(),
                }))}
                onChange={(value) =>
                  setDns((p) => ({ ...p, dns_query_timeout: Number(value) }))
                }
              />{' '}
              seconds
            </td>
          </tr>
          <tr className="webui-table-row-content">
            <td>Retries</td>
            <td>
              <WebUiSelect
                className="w-[72px]"
                inputSize="sm"
                disabled={readonly}
                value={dns.dns_query_retries?.toString() ?? '1'}
                options={DNS_RETRY_OPTS.map((n) => ({
                  label: n.toString(),
                  value: n.toString(),
                }))}
                onChange={(value) =>
                  setDns((p) => ({ ...p, dns_query_retries: Number(value) }))
                }
              />
            </td>
          </tr>
          <tr className="webui-table-row-content">
            <td>DNS over TCP</td>
            <td>
              <WebUiInput
                type="checkbox"
                disabled={readonly}
                checked={Boolean(dns.dns_over_tcp)}
                onChange={(e) =>
                  setDns((p) => ({ ...p, dns_over_tcp: e.target.checked }))
                }
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div>
        <WebUiButton
          disabled={readonly}
          onClick={() =>
            runSet(
              () =>
                postWebUiApi({
                  service: 'system',
                  access: 'set',
                  command: 'dnsclient',
                  parameters: dnsParams,
                }),
              'DNS client updated.',
            )
          }
        >
          Set DNS Client
        </WebUiButton>
      </div>
    </div>
  );
}
